import { REQUISITION_STATUS_LABEL } from "@/ui/vocabulary";
import { FilterRow, ActiveFilters } from "@/ui/FilterRow";
import { useId, useMemo, useState } from "react";
import {
  adminArchiveRequisition,
  adminArchiveSearch,
  adminPlaceLegalHold,
  adminReleaseLegalHold,
  archiveOpen,
  auditQuery,
  requisitionReopen,
} from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { BRAND_LABEL } from "@/views/Requests";
import { PHASE_LABEL } from "@/views/PhaseTargets";
import { MISSING, Banner, Empty, Field, KV, Pager, Pill, ReasonDialog, Receipt, RecordFields, Skeleton, Table, fmtDate, personLabel, recordLabel } from "@/ui";
import { McRoot } from "@/mc";
import { ArchivedBanner } from "@/views/b9/archive/ArchivedBanner";
import { closedReasonOf, withClosedReason } from "@/views/b9/archive/chips";
import { ARCHIVE_CHIP_LABELS, ARCHIVE_LABELS } from "@/views/b9/archive/labels";
import { CLOSED_REASONS, type ClosedReason } from "@/views/b9/archive/types";

const str = (v: unknown) => (v == null ? MISSING : String(v));
export function AdminArchive() {
  const [query, setQuery] = useState("");
  const [pages, setPages] = useState<(string | null)[]>([null]);
  // B9 S12 (D9): "admin.archive_search gains closed_reason (filled, cancelled)"; absent from the input when not chosen.
  const [closedReason, setClosedReason] = useState<ClosedReason | null>(null);
  const q = useRead(["archive-search", query, closedReason, pages.at(-1)], () => adminArchiveSearch(withClosedReason({ query: query || null, limit: 25, cursor: pages.at(-1) ?? null }, closedReason)));
  const [opened, setOpened] = useState<string | null>(null);
  const [action, setAction] = useState<{ kind: "archive" | "place" | "release" | "reopen"; id: string } | null>(null);
  const [entity, setEntity] = useState<"requisition" | "application" | "candidate" | "person">("requisition");
  const [entityId, setEntityId] = useState("");
  const [order, setOrder] = useState("");
  const archive = useWrite(adminArchiveRequisition, [["archive-search"], ["requisitions"]]);
  const place = useWrite(adminPlaceLegalHold, [["archive-search"]]);
  const release = useWrite(adminReleaseLegalHold, [["archive-search"]]);
  const reopen = useWrite(requisitionReopen, [["archive-search"], ["requisitions"]]);
  function open(kind: NonNullable<typeof action>["kind"], id = "") {
    archive.reset();
    place.reset();
    release.reset();
    reopen.reset();
    setOrder("");
    setAction({ kind, id });
  }
  return (
    <section>
      <h2 className="sec">Archive</h2>
      <p className="muted">There is no erasure control. A Legal order is executed outside the application.</p>
      <FilterRow><Field label="Search archives" id="archive-query">
        <div className="search-control"><span aria-hidden="true">⌕</span><input
          id="archive-query"
          placeholder="Requisition number or title"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPages([null]);
          }}
        /></div>
      </Field>
      <Field label={ARCHIVE_LABELS.reason} id="archive-closed-reason">
        <select id="archive-closed-reason" value={closedReason ?? ""} onChange={(e) => { setClosedReason(closedReasonOf(e.target.value)); setPages([null]); }}>
          <option value="">{ARCHIVE_CHIP_LABELS.archived}</option>
          {CLOSED_REASONS.map((reason) => <option key={reason} value={reason}>{ARCHIVE_CHIP_LABELS[reason]}</option>)}
        </select>
      </Field></FilterRow>
      <ActiveFilters values={{ query, closed_reason: closedReason }} labels={{ query: "Search", closed_reason: ARCHIVE_LABELS.reason }} display={{ filled: ARCHIVE_CHIP_LABELS.filled, cancelled: ARCHIVE_CHIP_LABELS.cancelled }} clear={(key) => { if (key === "closed_reason") setClosedReason(null); else setQuery(""); setPages([null]); }} />
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {archive.data && (
        <Receipt>
          Archived {archive.data.requisition_id} · snapshot {archive.data.snapshot_id}
        </Receipt>
      )}
      {place.isSuccess && <Receipt>Legal hold placed · {place.data.id}</Receipt>}
      {release.isSuccess && <Receipt>Legal hold released; the order and history are retained.</Receipt>}
      {reopen.isSuccess && (
        <Receipt>
          Reopen requested · HR Business Partner approval is required. <RecordFields variant="document" value={reopen.data} />
        </Receipt>
      )}
      {q.data && (
        <>
          <h3 className="sec">Due to archive</h3>
          {!q.data.pending?.length ? (
            <Empty heading="Nothing to archive">No closed requisitions waiting to archive.</Empty>
          ) : (
            <Table
              label="Due to archive"
              head={
                <tr>
                  <th>Requisition</th>
                  <th>Due</th>
                  <th>Archive gate</th>
                  <th>Action</th>
                </tr>
              }
            >
              {q.data.pending.map((r) => (
                <tr key={str(r.id)}>
                  <td>
                    <a href={`#requisitions/${r.id}`}>
                      {str(r.id)} · {str(r.title)}
                    </a>
                  </td>
                  <td>{fmtDate(r.due_at as string | null, true)}</td>
                  <td>
                    {r.blocked ? (
                      <Pill tone="warn">
                        {Array.isArray(r.hold_ids) && r.hold_ids.length
                          ? "Held back · legal hold"
                          : `held back: ${r.outstanding_count} dispositions outstanding`}
                      </Pill>
                    ) : (
                      <Pill tone="ok">Ready</Pill>
                    )}
                    {Array.isArray(r.hold_ids) && r.hold_ids.length > 0 && <div className="muted mono">{r.hold_ids.join(" · ")}</div>}
                  </td>
                  <td>
                    <button className="btn btn--sm" onClick={() => open("archive", str(r.id))}>
                      Archive now
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <h3 className="sec">Archived <span className="sec__n" role="status">{q.data.items.length} results</span></h3>
          {!q.data.items.length ? (
            <Empty heading={query ? "No archived requisitions match that search" : "No archived requisitions yet"} action={query ? <button className="btn" onClick={() => { setQuery(""); setPages([null]); }}>Clear search</button> : undefined}>{query ? "Try a requisition number or title." : "Retained requisitions appear here after a closed requisition is archived."}</Empty>
          ) : (
            <Table
              label="Archived requisitions"
              head={
                <tr>
                  <th>Requisition</th>
                  <th>Archived</th>
                  <th>Reason</th>
                  <th>Action</th>
                </tr>
              }
            >
              {q.data.items.map((r) => (
                <tr key={str(r.id)}>
                  <td>
                    <button className="link" onClick={() => setOpened(str(r.id))}>
                      {str(r.id)} · {str(r.title)}
                    </button>
                  </td>
                  <td>{fmtDate(r.archived_at as string, true)}</td>
                  <td className="long">{closedReasonOf(r.closed_reason as string | null) && <Pill tone="muted">{ARCHIVE_CHIP_LABELS[closedReasonOf(r.closed_reason as string | null)!]}</Pill>} {str(r.archive_reason)}</td>
                  <td>
                    <button className="btn btn--sm" onClick={() => open("reopen", str(r.id))}>
                      Request reopen
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <Pager count={q.data.items.length} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor]) : undefined}
          />
          {opened && (
            <section className="sheet">
              <button className="btn" onClick={() => setOpened(null)}>
                Close snapshot
              </button>
              <OpenedArchiveBanner id={opened} onReopen={() => open("reopen", opened)} />
              <ArchiveSnapshot id={opened} embedded />
            </section>
          )}
          <div className="hrow">
            <h3 className="sec">Legal holds</h3>
            <button className="btn" onClick={() => open("place")}>
              Place legal hold
            </button>
          </div>
          {!q.data.legal_holds?.length ? (
            <Empty heading="No holds are in force">No legal holds.</Empty>
          ) : (
            <Table
              label="Legal holds"
              head={
                <tr>
                  <th>Entity / hold</th>
                  <th>Written order</th>
                  <th>Placed / released</th>
                  <th>State</th>
                  <th>Action</th>
                </tr>
              }
            >
              {q.data.legal_holds.map((h) => (
                <tr key={str(h.id)}>
                  <td>
                    {str(h.entity_type)} · {str(h.entity_id)}
                    <div className="muted">{str(h.id)}</div>
                  </td>
                  <td>
                    {str(h.order_reference)}
                    {h.release_order_reference != null && <div>Release: {str(h.release_order_reference)}</div>}
                  </td>
                  <td>
                    {fmtDate(h.placed_at as string, true)}
                    {h.released_at != null && <div>{fmtDate(h.released_at as string, true)}</div>}
                  </td>
                  <td>
                    <Pill tone={h.released_at ? "muted" : "warn"}>{h.released_at ? "Released" : "Active"}</Pill>
                  </td>
                  <td>
                    {!h.released_at && (
                      <button className="btn btn--sm" onClick={() => open("release", str(h.id))}>
                        Release
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
      <ReasonDialog
        validateOnSubmit={action?.kind === "place"}
        title={
          action?.kind === "place"
            ? "Place legal hold"
            : action?.kind === "release"
              ? "Release legal hold"
              : action?.kind === "reopen"
                ? "Request reopen"
                : "Archive now"
        }
        confirm={
          action?.kind === "place" ? "Place hold" : action?.kind === "release" ? "Release hold" : action?.kind === "reopen" ? "Request reopen" : "Archive now"
        }
        open={!!action}
        onClose={() => setAction(null)}
        busy={archive.isPending || place.isPending || release.isPending || reopen.isPending}
        error={archive.error ?? place.error ?? release.error ?? reopen.error}
        onConfirm={(reason) => {
          const onSuccess = () => setAction(null);
          if (action?.kind === "archive") archive.mutate({ requisition_id: action.id, reason }, { onSuccess });
          if (action?.kind === "place") place.mutate({ entity_type: entity, entity_id: entityId, order_reference: order, reason }, { onSuccess });
          if (action?.kind === "release") release.mutate({ hold_id: action.id, release_order_reference: order, reason }, { onSuccess });
          if (action?.kind === "reopen") reopen.mutate({ requisition_id: action.id, kind: "other", reason }, { onSuccess });
        }}
      >
        {action?.kind === "place" && (
          <>
            <p>A hold blocks archiving and reopening until it is released.</p>
            <Field label="Entity type" required id="hold-type">
              <select id="hold-type" value={entity} onChange={(e) => setEntity(e.target.value as typeof entity)}>
                {["requisition", "application", "candidate", "person"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Entity" required id="hold-entity">
              <input id="hold-entity" required value={entityId} onChange={(e) => setEntityId(e.target.value)} />
            </Field>
          </>
        )}
        {(action?.kind === "place" || action?.kind === "release") && (
          <Field label={action.kind === "release" ? "Release order reference" : "Written order reference"} required id="hold-order">
            <input id="hold-order" required value={order} onChange={(e) => setOrder(e.target.value)} />
          </Field>
        )}
        {action?.kind === "archive" && <p>{action.id}: retained evidence becomes read-only. Open worklists and legal holds prevent archive.</p>}
      </ReasonDialog>
    </section>
  );
}
/** B9 S12 (D18): the opened result's banner, from the snapshot it reads (date and content hash); Reopen opens this page's
 *  reopen request, the host's dialog. The server decides reopen; the V1 page offered it on every row. */
function OpenedArchiveBanner({ id, onReopen }: { id: string; onReopen: () => void }) {
  const { can } = useSession();
  const q = useRead(["archive-open", id], () => archiveOpen({ entity_type: "requisition", entity_id: id }));
  if (!q.data) return null;
  return <McRoot><ArchivedBanner requisitionId={id} archivedAt={q.data.taken_at} contentHash={q.data.content_hash} canReopen={can("requisition.reopen")} onReopen={onReopen} /></McRoot>;
}

export function ArchiveSnapshot({ id, embedded = false }: { id: string; embedded?: boolean }) {
  const { can } = useSession();
  const q = useRead(["archive-open", id], () => archiveOpen({ entity_type: "requisition", entity_id: id }));
  const receipt = useRead(["archive-receipt", id], () => auditQuery({ entity_type: "requisition", entity_id: id, limit: 100 }), {
    enabled: can("audit.query"),
  });
  const payload = q.data?.payload ?? {};
  const Heading = embedded ? "h2" : "h1";
  return (
    <section className="archive-document">
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          <div className="callout admin-archived" role="status">
            Archived {fmtDate(q.data.taken_at, true)} · read-only
          </div>
          <Heading className={embedded ? "sec sec--plain" : "page"}>{id} · Archived snapshot</Heading>
          <KV
            wide={["Reason"]}
            items={[
              ["Archived by", q.data.taken_by_name || q.data.taken_by_id ? personLabel(q.data.taken_by_name, q.data.taken_by_id) : "Not recorded"],
              ["Access", ({ "P-REQ.full": "Full requisition record", "P-CAND.identity": "Recruiter record", "P-CAND.review": "Hiring Manager record", "P-CAND.hrbp": "HR Business Partner record", "P-REQ.panelist + P-SC.author": "Your panel evidence" } as Record<string, string>)[q.data.projection] ?? "Scoped retained record"],
              ["Reason", q.data.reason],
            ]}
          />
          <details>
            <summary>Archive receipt</summary>
            <Banner error={receipt.error} />
            {receipt.data ? (
              <RecordFields variant="document" value={receipt.data.items.filter((r) => ["admin.archive_requisition", "requisition.archive"].includes(r.tool))} />
            ) : (
              <RecordFields variant="document" value={{ snapshot_id: q.data.snapshot_id, taken_by: q.data.taken_by_name || q.data.taken_by_id ? personLabel(q.data.taken_by_name, q.data.taken_by_id) : "Not recorded", taken_at: q.data.taken_at, reason: q.data.reason }} />
            )}
          </details>
          <SnapshotDocument payload={payload} embedded={embedded} snapshotId={q.data.snapshot_id} contentHash={q.data.content_hash} archivedBy={q.data.taken_by_id} />
        </>
      )}
    </section>
  );
}

const ARCHIVE_SECTIONS: Record<string, string> = {
  header: "Record header", clocks: "Phase clocks", counts: "Counts at archive", files: "Retained files",
  seats: "People at archive", checkpoints: "Checkpoints", applications: "Applications", packets: "Interview packets",
  scorecards: "Scorecards", summary_sheets: "Summary Sheets", exceptions: "Process exceptions",
};
const technicalKey = /^(?:id|.*_ids?|hash|.*_hash)$/;

/** Preserve the projection exactly, separating its technical identifiers from the readable document. */
function splitArchive(value: unknown): { record: unknown; technical: unknown } {
  if (Array.isArray(value)) {
    const children = value.map(splitArchive);
    const records = children.map((child, i) => child.record ?? value[i]);
    return { record: records, technical: children.some((child) => child.technical !== undefined) ? children.map((child, i) => ({ record: i + 1, details: child.technical ?? "No technical details" })) : undefined };
  }
  if (!value || typeof value !== "object") {
    if (typeof value === "string" && /^p_[\w]+$/.test(value)) return { record: value, technical: value };
    if (typeof value === "string" && / · p_[\w]+$/.test(value)) return { record: value.replace(/ · p_[\w]+$/, ""), technical: value };
    return { record: value, technical: undefined };
  }
  const record: Record<string, unknown> = {}, technical: Record<string, unknown> = {};
  const source = value as Record<string, unknown>;
  for (const [key, child] of Object.entries(source)) {
    if (key.endsWith("_name") && (`${key.slice(0, -5)}_id` in source || key.slice(0, -5) in source)) continue;
    if (`${key}_name` in source && typeof child === "string") {
      technical[key] = child;
      record[key] = splitArchive(personLabel(source[`${key}_name`] as string | null, child)).record;
      continue;
    }
    // Collections recurse even when their name includes a technical suffix. References retain a readable
    // counterpart (or their raw id) so a missing directory entry never erases an author or application.
    if (technicalKey.test(key) && (child == null || typeof child !== "object")) {
      technical[key] = child;
      if (key !== "id" && key.endsWith("_id") && child != null) {
        const base = key.slice(0, -3), name = source[`${base}_name`];
        record[base] = splitArchive(typeof name === "string" ? personLabel(name, String(child)) : child).record;
      }
      continue;
    }
    const parts = splitArchive(child);
    if (parts.record !== undefined) record[key] = parts.record;
    if (parts.technical !== undefined && !(Array.isArray(parts.technical) && !parts.technical.length)) technical[key] = parts.technical;
  }
  return { record: Object.keys(value).length && !Object.keys(record).length ? undefined : record, technical: Object.keys(technical).length ? technical : undefined };
}

function SnapshotDocument({ payload, snapshotId, contentHash, archivedBy, embedded }: { embedded: boolean; payload: Record<string, unknown>; snapshotId: string; contentHash: string; archivedBy: string | null }) {
  const { record, technical } = useMemo(() => splitArchive(payload), [payload]);
  const clockId = useId();
  const SectionHeading = embedded ? "h3" : "h2";
  const sections = (record ?? {}) as Record<string, unknown>;
  const clocks = Array.isArray(sections.clocks) ? sections.clocks as Record<string, unknown>[] : [];
  const counts = sections.counts && typeof sections.counts === "object" ? Object.entries(sections.counts) : [];
  const populated = counts.filter(([, count]) => count !== 0), zero = counts.filter(([, count]) => count === 0);
  const remainder = Object.keys(sections).filter((key) => !["header", "clocks", "counts", "files"].includes(key));
  return <>
    {"clocks" in sections && <section className="archive-section"><SectionHeading className="sec sec--plain">Phase clocks</SectionHeading>
      {clocks.length ? <Table label="Phase clocks" head={<tr><th scope="col">Phase</th><th scope="col" id={`${clockId}-target`}>Target</th><th scope="col" id={`${clockId}-held`}>Held</th><th scope="col">Started</th><th scope="col">Stopped</th></tr>}>
        {clocks.map((clock, i) => <tr key={i}><th scope="row">{PHASE_LABEL[String(clock.phase_key)] ?? recordLabel(String(clock.phase_key ?? "Phase"))}</th><td headers={`${clockId}-target`}>{clock.target_days == null ? MISSING : `${clock.target_days} days`}</td><td headers={`${clockId}-held`}>{clock.held_days == null ? MISSING : `${clock.held_days} days`}</td><td>{fmtDate(clock.started_at as string | null, true)}</td><td>{fmtDate(clock.stopped_at as string | null, true)}</td></tr>)}
      </Table> : <p className="muted">No phase clocks were retained.</p>}
    </section>}
    {"counts" in sections && <section className="archive-section"><SectionHeading className="sec sec--plain">Counts at archive</SectionHeading>
      {populated.length > 0 && <p className="archive-counts">{populated.map(([key, count], i) => <span key={key}>{i > 0 ? " · " : ""}<b>{String(count)}</b> {recordLabel(key).toLowerCase()}</span>)}</p>}
      {zero.length > 0 && <p className="muted">None: {zero.map(([key]) => recordLabel(key).toLowerCase()).join(" · ")}</p>}
    </section>}
    {sections.header != null && <section className="archive-section"><SectionHeading className="sec sec--plain">Record header</SectionHeading><RecordFields variant="document" value={archiveHeader(sections.header)} /></section>}
    {remainder.map((key) => <section className="archive-section" key={key}><SectionHeading className="sec sec--plain">{ARCHIVE_SECTIONS[key] ?? recordLabel(key)}</SectionHeading><RecordFields variant="document" value={sections[key]} /></section>)}
    {"files" in sections && <section className="archive-section" aria-label="Archived files"><SectionHeading className="sec sec--plain">Retained files</SectionHeading>{Array.isArray(sections.files) && sections.files.length ? <Table label="Retained files" head={<tr><th scope="col">File</th><th scope="col">Version</th><th scope="col">State</th><th scope="col">Retained for</th><th scope="col">SHA-256</th></tr>}>{sections.files.map((file: Record<string, unknown>, i) => <tr key={i}><th scope="row">{recordLabel(String(file.kind ?? "File"))} · {i + 1}</th><td>{String(file.version ?? "Not recorded")}</td><td>{recordLabel(String(file.status ?? "Not recorded"))}</td><td>{recordLabel(String(file.owner_type ?? "Record"))} · {String(file.owner ?? "Not recorded")}</td><td className="long mono">{String(file.sha256 ?? "Not recorded")}</td></tr>)}</Table> : <RecordFields variant="document" value={sections.files} />}</section>}
    <details className="archive-technical"><summary>Technical details</summary>
      <KV wide={["Content hash"]} items={[["Snapshot", snapshotId], ["Archived by ID", archivedBy], ["Content hash", <span className="archive-hash mono">{contentHash}</span>]]} />
      <RecordFields variant="document" value={technical} />
    </details>
  </>;
}

/** Domain vocabulary belongs in the document; free-text evidence is left verbatim. */
function archiveHeader(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [key,
    key === "brands" && Array.isArray(child) ? child.map((brand) => BRAND_LABEL[String(brand)] ?? brand)
      : key === "status" && typeof child === "string" ? REQUISITION_STATUS_LABEL[child] ?? recordLabel(child)
      : key === "posting_scope" && typeof child === "string" ? recordLabel(child)
        : child,
  ]));
}
